import {
  Asterisk,
  Bell,
  BellPlus,
  Brain,
  Briefcase,
  Calendar,
  Camera,
  Code,
  Cpu,
  Film,
  Gamepad2,
  Hand,
  Key,
  LayoutGrid,
  Lightbulb,
  MapPin,
  MessagesSquare,
  Mic,
  Route,
  Settings,
  Sparkles,
  Tag,
  Tv,
  Users,
  Vibrate,
  Volume2,
  Wifi,
  type LucideIcon,
} from "lucide-react";
import type { StoreListing } from "@/api/store.api";

/** Known tags in the order the phone's chip row shows them. */
export const STORE_TAGS = ["productivity", "communication", "navigation", "ai", "games", "developer", "media"] as const;

const TAG_LABELS: Record<(typeof STORE_TAGS)[number], { label: string; icon: LucideIcon }> = {
  productivity: { label: "Productivity", icon: Briefcase },
  communication: { label: "Communication", icon: MessagesSquare },
  navigation: { label: "Navigation", icon: Route },
  ai: { label: "AI", icon: Sparkles },
  games: { label: "Games", icon: Gamepad2 },
  developer: { label: "Developer", icon: Code },
  media: { label: "Media", icon: Film },
};

function isKnownTag(tag: string): tag is (typeof STORE_TAGS)[number] {
  return (STORE_TAGS as readonly string[]).includes(tag);
}

/** Known tags get a display name; developer-defined ones show as typed. */
export function tagLabel(tag: string | "all"): string {
  if (tag === "all") return "All";
  return isKnownTag(tag) ? TAG_LABELS[tag].label : tag;
}

export function tagIcon(tag: string): LucideIcon {
  return isKnownTag(tag) ? TAG_LABELS[tag].icon : Tag;
}

/** The tag shown next to the publisher: the first known one, else the first at all. */
export function primaryTag(listing: Pick<StoreListing, "tags">): string | null {
  return listing.tags.find(isKnownTag) ?? listing.tags[0] ?? null;
}

const PERMISSION_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  MICROPHONE: { label: "Microphone", icon: Mic },
  CAMERA: { label: "Camera", icon: Camera },
  CALENDAR: { label: "Calendar", icon: Calendar },
  LOCATION: { label: "Location", icon: MapPin },
  BACKGROUND_LOCATION: { label: "Background location", icon: MapPin },
  DISPLAY_MIRROR: { label: "Read glasses display", icon: Tv },
  READ_NOTIFICATIONS: { label: "Read notifications", icon: Bell },
  POST_NOTIFICATIONS: { label: "Send notifications", icon: BellPlus },
  API_KEYS: { label: "API keys", icon: Key },
  MEMORY: { label: "Memory", icon: Brain },
  AI: { label: "AI", icon: Sparkles },
  SPEAKERS: { label: "Speaker names", icon: Users },
  MANAGE_MINIAPPS: { label: "Start and stop miniapps", icon: LayoutGrid },
  PLATFORM_SETTINGS: { label: "Change glasses settings", icon: Settings },
  ALL: { label: "All permissions", icon: Asterisk },
};

export function permissionLabel(type: string): string {
  return PERMISSION_LABELS[type]?.label ?? type;
}

export function permissionIcon(type: string): LucideIcon {
  return PERMISSION_LABELS[type]?.icon ?? Asterisk;
}

const HARDWARE_LABELS: Record<string, { label: string; icon: LucideIcon }> = {
  CAMERA: { label: "Camera", icon: Camera },
  DISPLAY: { label: "Display", icon: Tv },
  MICROPHONE: { label: "Microphone", icon: Mic },
  SPEAKER: { label: "Speaker", icon: Volume2 },
  IMU: { label: "Motion sensor", icon: Vibrate },
  BUTTON: { label: "Button", icon: Hand },
  LIGHT: { label: "Light", icon: Lightbulb },
  WIFI: { label: "Wi-Fi", icon: Wifi },
};

export function hardwareLabel(type: string): string {
  const known = HARDWARE_LABELS[type.toUpperCase()];
  if (known) return known.label;
  const raw = type.toLowerCase();
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function hardwareIcon(type: string): LucideIcon {
  return HARDWARE_LABELS[type.toUpperCase()]?.icon ?? Cpu;
}

export function typeLabel(type: StoreListing["type"]): string {
  switch (type) {
    case "background":
      return "Background only";
    case "dashboard":
      return "Glasses dashboard";
    default:
      return "Has a phone screen";
  }
}
